/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 * File Path: packages/sharedlib/src/data/calendar.data.ts
 */

import { getPrivateClient } from './common.data';

import type { ProviderInfo } from '@repo/common/types/Calendar';
import { LRUCache } from 'lru-cache';
import { get } from 'svelte/store';
import { userAuthStore } from '../stores/auth.store';

import { invalidate } from '@repo/sharedlib/src/lib/invalidate.util';

const cache = new LRUCache<string, ProviderInfo[]>({
  max: 5,
  ttl: 1000
});

export const fetchCalendars = async (): Promise<ProviderInfo[] | undefined> => {
  // Fetches calendars by proxying through the calendars' endpoint.
  const authToken = get(userAuthStore);
  if (!authToken?.idToken) {
    console.debug('No auth token found - cannot fetch calendars.', authToken);
    throw new Error('No auth token found - cannot fetch calendars.');
  }

  const client = getPrivateClient(authToken, { timeout: 10000 });
  const cacheKey = `fetchCalendars-${authToken.idToken.slice(0, 5)}`;
  const cached = cache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const data = (
    await client.get('/providers/calendars', {
      withCredentials: true
    })
  ).data as ProviderInfo[];

  const result = data.filter((providerInfo) => !!providerInfo);

  cache.set(cacheKey, result);
  return result;
};

// export const fetchCalendars = throttle(_fetchCalendars, 'fetchCalendars', 1000);

export const deleteCalendar = async (providerId: string): Promise<boolean> => {
  try {
    // Ensure the providerId is provided
    if (!providerId) {
      throw new Error('Provider ID is required to delete a calendar.');
    }

    const authToken = get(userAuthStore);
    if (!authToken?.idToken) {
      console.debug('No auth token found - cannot delete calendar.', authToken);
      throw new Error('No auth token found - cannot delete calendar.');
    }

    const client = getPrivateClient(authToken, { timeout: 5000 });

    // Call the DELETE endpoint
    const response = await client.delete(`/providers/calendars`, {
      params: { providerId }, // Pass the providerId as a query parameter
      withCredentials: true
    });

    // Check if the deletion was successful
    if (response.status === 200) {
      console.log(`Provider with ID ${providerId} deleted successfully.`);
      return true;
    }

    // Handle unexpected statuses
    console.warn(`Unexpected response status: ${response.status}`);
    return false;
  } catch (error) {
    console.error('Error deleting provider calendar:', error);
    return false;
  }
};

export const updateProviderData = async (
  providerId: string,
  data: Record<string, any>
): Promise<boolean> => {
  try {
    // Ensure the providerId is provided
    if (!providerId) {
      throw new Error('Provider ID is required to update provider data.');
    }

    const authToken = get(userAuthStore);
    if (!authToken?.idToken) {
      console.debug('No auth token found - cannot update provider data.', authToken);
      throw new Error('No auth token found - cannot update provider data.');
    }

    const client = getPrivateClient(authToken, { timeout: 5000 });

    // Call the PATCH endpoint
    const response = await client.patch('/providers/calendars', {
      providerId,
      data
    });

    // Check if the update was successful
    if (response.status === 200 && response.data?.success) {
      console.log(`Provider data updated successfully for provider ${providerId}.`);
      return true;
    }

    // Handle unexpected statuses
    console.warn(`Unexpected response status: ${response.status}`);
    return false;
  } catch (error) {
    console.error('Error updating provider data:', error);
    return false;
  }
};

export const updateEmailOptions = async (
  providerId: string,
  emailAddress: string,
  enabledOptions: string[]
): Promise<boolean> => {
  try {
    // Ensure the providerId is provided
    if (!providerId) {
      throw new Error('Provider ID is required to update email options.');
    }

    const authToken = get(userAuthStore);
    if (!authToken?.idToken) {
      console.debug('No auth token found - cannot update email options.', authToken);
      throw new Error('No auth token found - cannot update email options.');
    }

    const client = getPrivateClient(authToken, { timeout: 5000 });

    // Call the PATCH endpoint with email options data
    const response = await client.patch('/providers/calendars', {
      providerId,
      data: {
        enabledEmailOptions: {
          [emailAddress]: enabledOptions
        }
      }
    });

    // Check if the update was successful
    if (response.status === 200 && response.data?.success) {
      invalidate('user:email');
      return true;
    }

    // Handle unexpected statuses
    console.warn(`Unexpected response status: ${response.status}`);
    return false;
  } catch (error) {
    console.error('Error updating email options:', error);
    return false;
  }
};
